// Cross-app language persistence.
//
// Each SPA (public site, clinic app, patient portal…) lives on its own subdomain, so per-origin
// localStorage never carries the chosen language between them. We share it two ways:
//   1. a `lang` query param on cross-app links (deterministic for a single hop, works even across
//      different registrable domains), and
//   2. an `edlang` cookie scoped to the shared parent domain (sticky across all future navigations
//      between subdomains of the same site).
// Every app reads these on init and writes them whenever the user flips the switch.

export const SHARED_LANG_COOKIE = 'edlang';
const SUPPORTED = ['ro', 'en'] as const;
export type Lang = (typeof SUPPORTED)[number];

const isLang = (v: unknown): v is Lang => typeof v === 'string' && (SUPPORTED as readonly string[]).includes(v);

/** Parent domain shared by the site's subdomains (leading dot), or undefined for localhost/IP. */
export function sharedCookieDomain(): string | undefined {
  if (typeof location === 'undefined') return undefined;
  const host = location.hostname;
  if (host === 'localhost' || /^[\d.]+$/.test(host)) return undefined;
  // Strip a known app subdomain so clinic.x.y and x.y share one cookie; the public root keeps itself.
  // 'docs' belongs here for the same reason as the rest: the help site is a sibling SPA, and
  // without it the cookie is scoped to .docs.<site> and the reader's language silently stops
  // carrying back into the app they came from.
  const APP_PREFIXES = ['clinic', 'patient', 'operator', 'supplier', 'app', 'my', 'docs', 'imaging'];
  const parts = host.split('.');
  if (parts.length > 2 && APP_PREFIXES.includes(parts[0])) return '.' + parts.slice(1).join('.');
  return '.' + host;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

/** Explicit cross-app language: `?lang=` first, then the shared cookie. Null if neither is set. */
export function getSharedLang(): Lang | null {
  try {
    const q = new URLSearchParams(location.search).get('lang');
    if (isLang(q)) return q;
  } catch { /* no location */ }
  const c = readCookie(SHARED_LANG_COOKIE);
  return isLang(c) ? c : null;
}

/** Persist the choice everywhere: both localStorage keys in use + the shared parent-domain cookie. */
export function persistSharedLang(lng: string): void {
  if (!isLang(lng)) return;
  try {
    localStorage.setItem('lang', lng);       // public-app key
    localStorage.setItem('i18nextLng', lng); // i18next language-detector key (clinic/patient apps)
  } catch { /* storage blocked */ }
  try {
    const dom = sharedCookieDomain();
    const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; secure' : '';
    document.cookie =
      `${SHARED_LANG_COOKIE}=${lng}; path=/; max-age=31536000; samesite=lax${secure}${dom ? `; domain=${dom}` : ''}`;
  } catch { /* cookies blocked */ }
}

/** Append the active language to a cross-app URL so the destination adopts it immediately. */
export function withLang(url: string, lng?: string): string {
  let l = lng;
  if (!isLang(l)) l = getSharedLang() ?? (typeof localStorage !== 'undefined' ? localStorage.getItem('lang') ?? undefined : undefined);
  if (!isLang(l)) return url;
  try {
    const u = new URL(url, typeof location !== 'undefined' ? location.href : 'https://x');
    u.searchParams.set('lang', l);
    return u.toString();
  } catch {
    return url + (url.includes('?') ? '&' : '?') + 'lang=' + l;
  }
}
